import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { permsForPath, homeFor } from './lib/routePermissions';
import { Toaster } from './components/ui/sonner';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import EsqueciSenha from './pages/EsqueciSenha';
import Dashboard from './pages/Dashboard';
import Kanban from './pages/Kanban';
import Lista from './pages/Lista';
import Tarefas from './pages/Tarefas';
import Mapa from './pages/Mapa';
import Pacientes from './pages/Pacientes';
import VerPaciente from './pages/VerPaciente';
import NovoPaciente from './pages/NovoPaciente';
import NovaInternacao from './pages/NovaInternacao';
import PerfilInternacao from './pages/PerfilInternacao';
import AdminSeguranca from './pages/AdminSeguranca';
import Estatisticas from './pages/Estatisticas';
import Custos from './pages/Custos';
import AlterarSenha from './pages/AlterarSenha';
import Configuracoes from './pages/Configuracoes';
import AgenteIA from './pages/AgenteIA';
import Estoque from './pages/Estoque';
import SKU from './pages/SKU';
import Aprovisionamento from './pages/Aprovisionamento';
import Fisioterapia from './pages/Fisioterapia';
import Servicos from './pages/Servicos';
import MetricsDashboard from './pages/MetricsDashboard';
import MedicinaDireta from './pages/MedicinaDireta';
import Prescricao from './pages/Prescricao';
import Comodato from './pages/Comodato';
import Relatorios from './pages/Relatorios';
import Dispensacao from './pages/Dispensacao';
import Visitas from './pages/Visitas';
import Guias from './pages/Guias';
import RecebimentoGuias from './pages/RecebimentoGuias';
import FechamentoMensal from './pages/FechamentoMensal';
import PrimeirosAtendimentos from './pages/PrimeirosAtendimentos';
import AuditoriaFinanceira from './pages/AuditoriaFinanceira';
import AuditoriaProntuarios from './pages/AuditoriaProntuarios';
import Convenios from './pages/Convenios';
import Diarias from './pages/Diarias';

// Protected Route Component (RBAC: rota → permissão, ver lib/routePermissions.js)
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, can, permissoesEfetivas } = useAuth();
  const location = useLocation();
  
  if (loading || (isAuthenticated && permissoesEfetivas === null)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const required = permsForPath(location.pathname);
  if (required && required.length > 0 && !can(...required)) {
    const home = homeFor(can);
    return <Navigate to={home === location.pathname ? '/configuracoes' : home} replace />;
  }
  
  return children;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  useEffect(() => {
    const removeBadge = () => {
      const el = document.getElementById('emergent-badge');
      if (el) el.remove();
    };
    removeBadge();
    const observer = new MutationObserver(removeBadge);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/esqueci-senha" element={<EsqueciSenha />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/kanban" element={<ProtectedRoute><Kanban /></ProtectedRoute>} />
      <Route path="/lista" element={<ProtectedRoute><Lista /></ProtectedRoute>} />
      <Route path="/tarefas" element={<ProtectedRoute><Tarefas /></ProtectedRoute>} />
      <Route path="/prescricao" element={<ProtectedRoute><Prescricao /></ProtectedRoute>} />
      <Route path="/estatisticas" element={<ProtectedRoute><Estatisticas /></ProtectedRoute>} />
      <Route path="/custos" element={<ProtectedRoute><Custos /></ProtectedRoute>} />
      <Route path="/estoque" element={<ProtectedRoute><Estoque /></ProtectedRoute>} />
      <Route path="/sku" element={<ProtectedRoute><SKU /></ProtectedRoute>} />
      <Route path="/aprovisionamento" element={<ProtectedRoute><Aprovisionamento /></ProtectedRoute>} />
      <Route path="/fisioterapia" element={<ProtectedRoute><Fisioterapia /></ProtectedRoute>} />
      <Route path="/comodato" element={<ProtectedRoute><Comodato /></ProtectedRoute>} />
      <Route path="/relatorios" element={<ProtectedRoute><Relatorios /></ProtectedRoute>} />
      <Route path="/servicos" element={<ProtectedRoute><Servicos /></ProtectedRoute>} />
      <Route path="/mapa" element={<ProtectedRoute><Mapa /></ProtectedRoute>} />
      <Route path="/pacientes" element={<ProtectedRoute><Pacientes /></ProtectedRoute>} />
      <Route path="/pacientes/novo" element={<ProtectedRoute><NovoPaciente /></ProtectedRoute>} />
      <Route path="/pacientes/:pacienteId" element={<ProtectedRoute><VerPaciente /></ProtectedRoute>} />
      <Route path="/pacientes/:pacienteId/nova-internacao" element={<ProtectedRoute><NovaInternacao /></ProtectedRoute>} />
      <Route path="/pacientes/:pacienteId/internacao/:internacaoId" element={<ProtectedRoute><PerfilInternacao /></ProtectedRoute>} />
      
      {/* User Settings */}
      <Route path="/alterar-senha" element={<ProtectedRoute><AlterarSenha /></ProtectedRoute>} />
      <Route path="/configuracoes" element={<ProtectedRoute><Configuracoes /></ProtectedRoute>} />
      <Route path="/agente-ia" element={<ProtectedRoute><AgenteIA /></ProtectedRoute>} />
      <Route path="/metricas" element={<ProtectedRoute><MetricsDashboard /></ProtectedRoute>} />
          <Route path="/medicina-direta" element={<ProtectedRoute><MedicinaDireta /></ProtectedRoute>} />
      <Route path="/dispensacao" element={<ProtectedRoute><Dispensacao /></ProtectedRoute>} />
      <Route path="/visitas" element={<ProtectedRoute><Visitas /></ProtectedRoute>} />
      <Route path="/guias" element={<ProtectedRoute><Guias /></ProtectedRoute>} />
      <Route path="/recebimento-guias" element={<ProtectedRoute><RecebimentoGuias /></ProtectedRoute>} />
      <Route path="/fechamento-mensal" element={<ProtectedRoute><FechamentoMensal /></ProtectedRoute>} />
      <Route path="/diarias" element={<ProtectedRoute><Diarias /></ProtectedRoute>} />
      <Route path="/primeiros-atendimentos" element={<ProtectedRoute><PrimeirosAtendimentos /></ProtectedRoute>} />
      <Route path="/auditoria-financeira" element={<ProtectedRoute><AuditoriaFinanceira /></ProtectedRoute>} />
      <Route path="/financeiro/convenios" element={<ProtectedRoute><Convenios /></ProtectedRoute>} />
      <Route path="/auditoria-prontuarios" element={<ProtectedRoute><AuditoriaProntuarios /></ProtectedRoute>} />
      
      {/* Master Admin Only */}
      <Route path="/admin-seguranca" element={<ProtectedRoute><AdminSeguranca /></ProtectedRoute>} />
      
      {/* Redirect root to dashboard or login */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      
      {/* 404 - Redirect to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
